/**
 * PublicInfoPage — publiczny widok pojedynczej informacji ogólnej.
 *
 * Dostępna pod URL: /info/{info_id}
 * Nie wymaga logowania — używana do dzielenia się na Facebooku / Messenger itd.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Smartphone, ClipboardList, Sparkles, GraduationCap, Utensils, Tag,
  Calendar, User as UserIcon, ArrowLeft, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  app:         { label: 'Aplikacja',  Icon: Smartphone,    color: '#0ea5e9' },
  procedure:   { label: 'Procedura',  Icon: ClipboardList, color: '#a855f7' },
  new_feature: { label: 'Nowość',     Icon: Sparkles,      color: '#ea580c' },
  training:    { label: 'Szkolenie',  Icon: GraduationCap, color: '#22c55e' },
  menu:        { label: 'Menu',       Icon: Utensils,      color: '#f43f5e' },
  other:       { label: 'Inne',       Icon: Tag,           color: '#94a3b8' },
};

const LOCATION_LABELS = {
  krakow: 'Kraków',
  nowysacz: 'Nowy Sącz',
  grodek: 'Gródek nad Dunajcem',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const resolveImageUrl = (path) => {
  if (!path) return path;
  // Auto-fix legacy /uploads/general_info/ → /api/uploads/general_info/ (działa też w pełnych URL)
  let p = path;
  if (typeof p === 'string' && p.includes('/uploads/general_info/') && !p.includes('/api/uploads/general_info/')) {
    p = p.replace('/uploads/general_info/', '/api/uploads/general_info/');
  }
  if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('data:')) return p;
  if (p.startsWith('/uploads/')) return `${API}/api${p}`;
  return `${API}${p}`;
};

export default function PublicInfoPage() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/general-info/${id}`)
      .then((r) => { if (active) setInfo(r.data); })
      .catch((e) => {
        if (!active) return;
        setError(e?.response?.status === 404 ? 'Nie znaleziono informacji' : 'Błąd ładowania');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (info?.title) {
      document.title = `${info.title} · MR JERRY`;
    }
  }, [info]);

  const cat = info ? (CATEGORIES[info.category] || CATEGORIES.other) : null;
  const CatIcon = cat?.Icon;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.08) 0%, transparent 55%), linear-gradient(180deg, #0a0a14 0%, #14141f 100%)',
      color: '#f5f5f5',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      padding: '24px 16px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header / branding */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            color: '#a0a0a8', fontSize: 13, textDecoration: 'none',
            padding: '8px 14px', borderRadius: 999,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <ArrowLeft size={14} /> Strona główna
          </Link>
          <div style={{
            fontSize: 11, letterSpacing: '0.15em', fontWeight: 800, textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #d4af37, #f1d98a)',
            WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          }}>
            MR JERRY · Komunikat
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 48, color: '#a0a0a8', fontSize: 14 }}>Ładowanie...</div>
        )}

        {error && (
          <div style={{
            textAlign: 'center', padding: 48,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 16, color: '#ef4444', fontSize: 15, fontWeight: 600,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && info && (
          <article data-testid="public-info-content" style={{
            background: '#12121a',
            border: '1px solid rgba(212,175,55,0.18)',
            borderRadius: 18,
            padding: 28,
            boxShadow: '0 30px 80px -30px rgba(0,0,0,0.6)',
          }}>
            {/* Category + location */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 999,
                background: `${cat.color}1f`, color: cat.color,
                border: `1px solid ${cat.color}55`,
                fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                {CatIcon && <CatIcon size={12} />} {cat.label}
              </span>
              {info.pinned && (
                <span style={{
                  padding: '5px 12px', borderRadius: 999,
                  background: 'rgba(234,88,12,0.12)', color: '#ea580c',
                  border: '1px solid rgba(234,88,12,0.4)',
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>📌 Przypięte</span>
              )}
              <span style={{
                marginLeft: 'auto', fontSize: 12, color: '#a0a0a8',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                📍 {LOCATION_LABELS[info.location] || info.location}
              </span>
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: 28, fontWeight: 900, margin: '0 0 14px',
              letterSpacing: '-0.02em', lineHeight: 1.2, color: '#fff',
            }}>{info.title}</h1>

            {/* Meta */}
            <div style={{ display: 'flex', gap: 14, color: '#a0a0a8', fontSize: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={12} /> {fmtDate(info.createdAt)}
              </span>
              {info.author && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <UserIcon size={12} /> {info.author}
                </span>
              )}
            </div>

            {/* Description */}
            {info.description && (
              <div style={{
                fontSize: 15, lineHeight: 1.65,
                color: '#e8e8ec', whiteSpace: 'pre-wrap',
                marginBottom: 22,
              }}>{info.description}</div>
            )}

            {/* Video */}
            {info.videoUrl && (
              <div style={{ marginBottom: 22, borderRadius: 14, overflow: 'hidden', background: '#000', aspectRatio: '16 / 9' }}>
                {info.videoUrl.includes('embed') || info.videoUrl.includes('player.vimeo.com') ? (
                  <iframe
                    src={info.videoUrl}
                    title={info.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ width: '100%', height: '100%', border: 0 }}
                  />
                ) : (
                  <video src={resolveImageUrl(info.videoUrl)} controls style={{ width: '100%', height: '100%' }} />
                )}
              </div>
            )}

            {/* Images grid */}
            {info.images?.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(info.images.length, 2)}, 1fr)`,
                gap: 10, marginBottom: 20,
              }}>
                {info.images.map((src, i) => (
                  <a
                    key={i}
                    href={resolveImageUrl(src)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block', borderRadius: 12, overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.06)',
                      aspectRatio: '4 / 3',
                    }}
                  >
                    <img
                      src={resolveImageUrl(src)}
                      alt={`Załącznik ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </a>
                ))}
              </div>
            )}

            {/* Footer CTA */}
            <div style={{
              marginTop: 28, paddingTop: 18,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center',
            }}>
              <div style={{ fontSize: 12, color: '#a0a0a8' }}>
                System zarządzania <b style={{ color: '#d4af37' }}>MR JERRY</b>
              </div>
              <a
                href="/"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, #d4af37, #f1d98a)',
                  color: '#0a0a14', fontSize: 12, fontWeight: 800,
                  textDecoration: 'none', letterSpacing: '0.03em',
                }}
              >
                Zaloguj się <ExternalLink size={12} />
              </a>
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
